import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdSchedule, 
  MdEdit, 
  MdDelete, 
  MdVisibility,
  MdFilterList,
  MdSearch,
  MdAdd,
  MdSchool,
  MdCalendarToday,
  MdPeople,
  MdLocationOn
} from 'react-icons/md';

/**
 * Timetables List Component
 * Displays all timetables with filtering options
 */
function TimetablesList() {
  const navigate = useNavigate();
  const { getToken } = useAttendanceAuth();
  const [timetables, setTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filters, setFilters] = useState({
    state: 'all',
    campus: '',
    batch_id: '',
    search: ''
  });

  // Helper function to fix academic year display (replace PG with UG)
  const formatAcademicYear = (academicYear) => {
    if (!academicYear) return '';
    // Replace "PG" with "UG" in the academic year string
    return academicYear.replace(/\s*-\s*PG\s*$/i, ' - UG').replace(/\s*PG\s*$/i, ' UG');
  };

  useEffect(() => {
    fetchTimetables();
  }, [filters.state, filters.campus, filters.batch_id]);

  useEffect(() => {
    const loadCampuses = async () => {
      const result = await attendanceAPI.getCampuses(getToken());
      if (result.success) setCampuses(result.campuses || []);
    };
    loadCampuses().catch(err => console.error('Error loading campuses:', err));
  }, []);

  useEffect(() => {
    if (!filters.campus) {
      setBatches([]);
      return;
    }
    attendanceAPI.getBatches({ campus: filters.campus }, getToken())
      .then(result => setBatches(result.success ? (result.batches || []) : []))
      .catch(err => {
        console.error('Error loading campus batches:', err);
        setBatches([]);
      });
  }, [filters.campus]);

  const fetchTimetables = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const params = {};
      
      if (filters.state !== 'all') {
        params.state = filters.state;
      }
      if (filters.batch_id) {
        params.batch_id = parseInt(filters.batch_id);
      }
      if (filters.campus) params.campus = filters.campus;
      if (filters.search) {
        params.search = filters.search;
      }

      const result = await attendanceAPI.getTimetables(params, token);
      
      if (result.success && result.timetables) {
        setTimetables(result.timetables);
      } else {
        setTimetables([]);
      }
    } catch (err) {
      console.error('Error fetching timetables:', err);
      setError(err.message || 'Failed to load timetables');
      setTimetables([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (timetableId, timetableName) => {
    if (!window.confirm(`Are you sure you want to delete "${timetableName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = getToken();
      const result = await attendanceAPI.deleteTimetable(timetableId, token);
      
      if (result.success) {
        fetchTimetables(); // Refresh list
      } else {
        alert(result.message || 'Failed to delete timetable');
      }
    } catch (err) {
      console.error('Error deleting timetable:', err);
      alert(err.message || 'Failed to delete timetable');
    }
  };

  const getStateBadge = (state) => {
    const stateLower = state?.toLowerCase() || 'draft';
    switch (stateLower) {
      case 'active':
        return (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'ended':
        return (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Ended
          </span>
        );
      case 'draft':
      default:
        return (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Draft
          </span>
        );
    }
  };

  const filteredTimetables = timetables.filter(timetable => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const name = (timetable.name || '').toLowerCase();
      const batches = (timetable.batches || []).map(b => (b.name || b).toLowerCase()).join(' ');
      const campus = (timetable.campus_name || timetable.campus || '').toLowerCase();
      return name.includes(searchLower) || batches.includes(searchLower) || campus.includes(searchLower);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading timetables...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <MdSchedule className="text-3xl text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Timetables</h1>
              <p className="text-gray-600 text-sm mt-1">Manage class schedules and timetables</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/attendance/faculty-admin/timetable/create')}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center space-x-2 shadow-lg"
          >
            <MdAdd />
            <span>Create Timetable</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search timetables..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <select
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All States</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="ended">Ended</option>
            </select>

            <select
              value={filters.campus}
              onChange={(e) => setFilters({ ...filters, campus: e.target.value, batch_id: '' })}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Campuses</option>
              {campuses.map(campus => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
            </select>

            <select
              value={filters.batch_id}
              onChange={(e) => setFilters({ ...filters, batch_id: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              disabled={!filters.campus}
              <option value="">{filters.campus ? 'All Batches' : 'Select campus first'}</option>
              {batches.map(batch => <option key={batch.id} value={batch.id}>{batch.name || batch.code}</option>)}
            </select>

            <button
              onClick={fetchTimetables}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition flex items-center justify-center space-x-2"
            >
              <MdFilterList />
              <span>Apply Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Timetables List */}
      {filteredTimetables.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <MdSchedule className="text-5xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Timetables Found</h3>
          <p className="text-gray-500 mb-6">
            {filters.search || filters.state !== 'all' || filters.campus || filters.batch_id
              ? 'No timetables match your filters.'
              : 'Get started by creating your first timetable.'}
          </p>
          {!filters.search && filters.state === 'all' && !filters.campus && !filters.batch_id && (
            <button
              onClick={() => navigate('/attendance/faculty-admin/timetable/create')}
              className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition font-semibold"
            >
              Create First Timetable
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {filteredTimetables.map((timetable) => (
            <div
              key={timetable.id}
              className="bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {timetable.name || 'Unnamed Timetable'}
                  </h3>
                  {getStateBadge(timetable.state)}
                </div>
              </div>

              <div className="space-y-3 mb-4">
                {(timetable.campus_name || timetable.campus) && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MdLocationOn className="text-gray-400" />
                    <span><span className="font-medium">Campus:</span> {timetable.campus_name || timetable.campus}</span>
                  </div>
                )}
                {timetable.batches && timetable.batches.length > 0 && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MdPeople className="text-gray-400" />
                    <span>
                      <span className="font-medium">Batches:</span>{' '}
                      {Array.isArray(timetable.batches)
                        ? timetable.batches.map(b => b.name || b).join(', ')
                        : timetable.batches}
                    </span>
                  </div>
                )}

                {timetable.academic_year && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MdSchool className="text-gray-400" />
                    <span>
                      <span className="font-medium">Academic Year:</span> {formatAcademicYear(timetable.academic_year)}
                    </span>
                  </div>
                )}

                {timetable.semester && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MdSchedule className="text-gray-400" />
                    <span>
                      <span className="font-medium">Semester:</span> {timetable.semester}
                    </span>
                  </div>
                )}

                {timetable.subject_count !== undefined && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MdSchedule className="text-gray-400" />
                    <span>
                      <span className="font-medium">Subjects:</span> {timetable.subject_count}
                    </span>
                  </div>
                )}

                {timetable.start_date && timetable.end_date && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <MdCalendarToday className="text-gray-400" />
                    <span>
                      {new Date(timetable.start_date).toLocaleDateString()} - {new Date(timetable.end_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex space-x-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => navigate(`/attendance/faculty-admin/timetable/${timetable.id}`)}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center justify-center space-x-2"
                >
                  <MdVisibility />
                  <span>View</span>
                </button>
                
                {(timetable.state === 'draft' || timetable.state === 'active') && (
                  <button
                    onClick={() => navigate(`/attendance/faculty-admin/timetable/${timetable.id}/edit`)}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition flex items-center space-x-2"
                  >
                    <MdEdit />
                  </button>
                )}
                {timetable.state === 'draft' && (
                  <button
                    onClick={() => handleDelete(timetable.id, timetable.name)}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition flex items-center space-x-2"
                  >
                    <MdDelete />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {filteredTimetables.length > 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Showing <span className="font-semibold">{filteredTimetables.length}</span> timetable{filteredTimetables.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}

export default TimetablesList;




